import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { Loader2 } from "lucide-react";

import { HoverHintDefinitionGrid } from "@/components/ui/HoverHintDefinitionGrid";
import {
  fetchWorkOrderDetail,
  workOrderDetailQueryKey,
  WORK_ORDER_DETAIL_STALE_MS,
} from "@/pages/manufacturing/workOrderDetailWorkspace";
import type { WorkOrderDetail } from "@/pages/manufacturing/workOrdersPageModel";
import {
  effectiveWorkOrderOrderedQuantity,
  effectiveWorkOrderReceivedQuantity,
  effectiveWorkOrderStatus,
} from "@/pages/manufacturing/workOrdersPageModel";

function detailField(value: string | number | null | undefined): ReactNode {
  if (value == null) return "—";
  const text = String(value).trim();
  return text === "" ? "—" : text;
}

function WorkOrderDetailHoverHintBody({ detail }: { detail: WorkOrderDetail }): JSX.Element {
  const received = effectiveWorkOrderReceivedQuantity(detail);
  const ordered = effectiveWorkOrderOrderedQuantity(detail);
  const status = effectiveWorkOrderStatus(detail);

  return (
    <HoverHintDefinitionGrid
      rows={[
        { label: "Work order", value: detailField(detail.number) },
        { label: "SKU", value: <span className="font-mono">{detailField(detail.sku)}</span> },
        { label: "Status", value: detailField(status) },
        {
          label: "Progress",
          value: (
            <span className="tabular-nums">
              {received.toLocaleString()}/{Number(ordered).toLocaleString()}
            </span>
          ),
        },
        {
          label: "Reference",
          value: detailField(detail.mfr_reference_number ?? detail.mv_reference_number),
        },
        { label: "Location", value: detailField(detail.location_name) },
      ]}
    />
  );
}

export function WorkOrderDetailHoverHint({ buildOrderId }: { buildOrderId: number }): JSX.Element {
  const detailQuery = useQuery({
    queryKey: workOrderDetailQueryKey(buildOrderId),
    queryFn: () => fetchWorkOrderDetail(buildOrderId),
    staleTime: WORK_ORDER_DETAIL_STALE_MS,
  });

  if (detailQuery.isLoading) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-slate-600">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        Loading work order…
      </p>
    );
  }

  if (detailQuery.isError || detailQuery.data == null) {
    return <p className="text-xs text-slate-600">Could not load work order preview.</p>;
  }

  return <WorkOrderDetailHoverHintBody detail={detailQuery.data} />;
}
